import {
	AppIcon,
	AppScreen,
	AppSelector,
	ScreenSizeEnum,
	StackNavigation,
	toCurrency,
	useAppDevice,
	useAppTheme,
} from '@itrustcapital/ui';
import { useNavigation } from '@react-navigation/native';
import { Divider, Spinner, Text } from '@ui-kitten/components';
import {
	StakingScreen_GetDailyStakingEnrollments_dailyStakingEnrollments_data_stakingAmounts_stakeStakingAmounts as StakeAmounts,
	StakingScreen_GetDailyStakingEnrollments_dailyStakingEnrollments_data_stakingAmounts_unstakeStakingAmounts as UnstakeAmounts,
} from 'codegen/StakingScreen_GetDailyStakingEnrollments';
import { format, startOfDay } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { Card } from 'react-native-paper';

import { StakingFilterForm } from './StakingScreenFilterForm';
import { StakingScreenProvider, useStakingScreenState } from './StakingScreenProvider';

export function StakingScreen() {
	const styles = useCustomStyles();
	const dynamicStyles = useDynamicStyles();
	const navigation = useNavigation<StackNavigation>();
	const stakingState = useStakingScreenState();

	const availableCurrencies =
		stakingState.stakingBalances?.filter((b) => b.ticker !== 'DOT') ?? [];
	const [selectedCurrency, setSelectedCurrency] = useState<string>(
		availableCurrencies[0]?.ticker ?? ''
	);

	const selectedDate = stakingState.stakingForm.watch('createdAt') ?? new Date();
	const formattedDate = format(startOfDay(selectedDate), 'MM/dd/yyyy');

	useEffect(() => {
		navigation.setOptions({ header: () => null });
	}, []);

	useEffect(() => {
		if (availableCurrencies.length > 0 && !selectedCurrency) {
			setSelectedCurrency(availableCurrencies[0].ticker);
		}
	}, [availableCurrencies]);

	if (!stakingState.stakingBalances) {
		return (
			<AppScreen style={[styles.main.container, dynamicStyles.main.container]}>
				<Spinner size="medium" status="basic" />
			</AppScreen>
		);
	}

	const renderStakingGroup = (
		product: string,
		staked: number | undefined,
		unstaked: number | undefined,
		stakedCount: number | undefined,
		unstakedCount: number | undefined,
		ticker: string
	) => (
		<View>
			<View style={styles.header.totalsRow}>
				<Text category="h5" style={styles.header.text}>
					{product} Daily Staking Totals ({formattedDate})
				</Text>
			</View>
			{stakingState.loading ? (
				<View style={styles.main.spinner}>
					<Spinner size="medium" status="basic" />
				</View>
			) : (
				<View style={styles.top.cards}>
					{[
						{ label: 'Staking', value: staked, icon: 'corner-left-down' },
						{ label: 'Unstaking', value: unstaked, icon: 'corner-left-up' },
						{ label: 'New Stake Requests', value: stakedCount },
						{ label: 'New Unstake Requests', value: unstakedCount },
					].map((item, index) => (
						<Card key={index} style={[styles.top.card, dynamicStyles.top.card]}>
							{item.icon && (
								<View style={styles.top.cardContent}>
									<AppIcon lib="fe" name={item.icon} size={14} />
									<Text category="s1" style={styles.top.text}>
										{item.label}
									</Text>
								</View>
							)}
							{!item.icon && (
								<Text category="s1" style={styles.top.text}>
									{item.label}
								</Text>
							)}
							<Divider style={styles.top.divider} />
							<Text category="h3">
								{item.value ?? 0} {item.icon ? ticker : ''}
							</Text>
						</Card>
					))}
				</View>
			)}
		</View>
	);

	const selectedBalance = availableCurrencies.find((b) => b.ticker === selectedCurrency);

	if (!selectedBalance) {
		return (
			<AppScreen style={[styles.main.container, dynamicStyles.main.container]}>
				<View style={styles.header.container}>
					<Text category="h3">Staking Overview</Text>
				</View>
				<Text>No staking balances available</Text>
			</AppScreen>
		);
	}

	const ticker = selectedBalance.ticker;

	const getAmounts = (stakeAmounts: StakeAmounts[], unstakeAmounts: UnstakeAmounts[]) => {
		const stake = stakeAmounts?.find((x) => x.ticker === ticker);
		const unstake = unstakeAmounts?.find((x) => x.ticker === ticker);

		return {
			staked: stake?.principleAmount,
			unstaked: unstake?.totalAmount,
			stakedCount: stake?.count,
			unstakedCount: unstake?.count,
		};
	};

	const ira = getAmounts(
		stakingState.iraStakingAmounts ?? [],
		stakingState.iraUnstakingAmounts ?? []
	);
	const pca = getAmounts(
		stakingState.pcaStakingAmounts ?? [],
		stakingState.pcaUnstakingAmounts ?? []
	);
	const tpa = getAmounts(
		stakingState.tpaStakingAmounts ?? [],
		stakingState.tpaUnstakingAmounts ?? []
	);

	return (
		<AppScreen
			noMarginBottom
			noMaxWidth
			style={[styles.main.container, dynamicStyles.main.container]}
		>
			<StakingScreenProvider value={stakingState}>
				<View style={styles.main.content}>
					<View style={styles.header.container}>
						<Text category="h3">Staking Overview</Text>
					</View>

					<Card style={styles.main.card}>
						<View style={styles.filter.container}>
							<AppSelector
								closeOnSelect
								data={availableCurrencies
									.filter((x) => x.ticker !== 'DOT')
									.map((b) => b.ticker)}
								itemDisplay={(ticker: string) => ticker}
								label="Currency"
								value={selectedCurrency}
								onSelect={(value: string) => {
									setSelectedCurrency(value);
								}}
							/>
							<View>
								<StakingFilterForm />
							</View>
						</View>

						<View style={[styles.top.row, dynamicStyles.top.row]}>
							<View>
								<Text category="h1" style={styles.header.currencyText}>
									{ticker}
								</Text>
							</View>

							<View>
								<Text category="h2" style={styles.top.stakedText}>
									{selectedBalance.totalOverallBalance}
									<Text category="s1" style={styles.top.inlineSubtext}>
										Total Staked
									</Text>
								</Text>
								<Text
									category="h2"
									style={[styles.top.totalsText, dynamicStyles.top.totalValue]}
								>
									{toCurrency(selectedBalance.totalOverallValue, {
										showDollar: true,
									})}
									<Text category="s1" style={styles.top.inlineSubtext}>
										Total Value
									</Text>
								</Text>
							</View>
						</View>

						{renderStakingGroup(
							'IRA',
							ira.staked,
							ira.unstaked,
							ira.stakedCount,
							ira.unstakedCount,
							ticker
						)}
						{renderStakingGroup(
							'PCA',
							pca.staked,
							pca.unstaked,
							pca.stakedCount,
							pca.unstakedCount,
							ticker
						)}
						{renderStakingGroup(
							'TPA',
							tpa.staked,
							tpa.unstaked,
							tpa.stakedCount,
							tpa.unstakedCount,
							ticker
						)}
					</Card>
				</View>
			</StakingScreenProvider>
		</AppScreen>
	);
}

function useCustomStyles() {
	const theme = useAppTheme();

	return {
		main: StyleSheet.create({
			container: {
				marginBottom: 20,
			},
			content: {
				flex: 1,
			},
			card: {
				marginBottom: 8,
				paddingHorizontal: 18,
				paddingVertical: 18,
				backgroundColor: theme['color-basic-1000'],
			},
			spinner: {
				width: '100%',
				alignItems: 'center',
			},
		}),
		header: StyleSheet.create({
			container: {
				paddingBottom: 20,
				justifyContent: 'space-between',
				flexDirection: 'row',
				flexWrap: 'wrap',
			},
			text: {
				marginBottom: 8,
				color: theme['color-basic-600'],
			},
			totalsRow: {
				flexDirection: 'row',
				alignItems: 'flex-start',
				flexWrap: 'wrap',
				justifyContent: 'space-between',
				marginTop: 36,
			},
			currencyText: {
				color: theme['color-basic-600'],
				marginRight: 12,
			},
		}),
		filter: StyleSheet.create({
			container: {
				flexDirection: 'row',
				alignItems: 'center',
				justifyContent: 'space-between',
				marginBottom: 8,
				borderBottomColor: theme['color-basic-transparent-300'],
				borderBottomWidth: 1,
			},
		}),
		top: StyleSheet.create({
			row: {
				flexDirection: 'column',
				alignItems: 'flex-start',
				justifyContent: 'space-between',
			},
			inlineSubtext: {
				fontSize: 12,
				color: theme['color-basic-600'],
				marginLeft: 8,
			},
			stakedText: {
				textAlign: 'right',
			},
			totalsText: {
				textAlign: 'right',
			},
			text: {
				textAlign: 'left',
				marginBottom: 4,
			},
			card: {
				flex: 1,
				justifyContent: 'center',
				alignContent: 'center',
				maxWidth: 240,
				minWidth: 240,
				maxHeight: 200,
				borderWidth: 0.35,
				borderColor: theme['color-basic-700'],
				shadowRadius: 4,
				paddingVertical: 12,
				paddingHorizontal: 12,
				borderRadius: 8,
				backgroundColor: theme['color-basic-1000'],
			},
			cards: {
				flexDirection: 'row',
				alignContent: 'center',
				flexWrap: 'wrap',
				gap: 16,
			},
			cardContent: {
				flexDirection: 'row',
				alignItems: 'baseline',
			},
			divider: {
				height: 2,
				backgroundColor: theme['color-basic-transparent-300'],
			},
		}),
	};
}

function useDynamicStyles() {
	const appDevice = useAppDevice();
	const isScreenMd = appDevice.width >= ScreenSizeEnum.md;
	const theme = useAppTheme();

	return {
		main: StyleSheet.create({
			container: {
				flexDirection: isScreenMd ? 'row' : 'column',
			},
		}),
		top: StyleSheet.create({
			row: {
				flexDirection: isScreenMd ? 'row' : 'column',
				alignItems: isScreenMd ? 'center' : 'flex-start',
			},
			card: {
				maxWidth: isScreenMd ? 240 : '100%',
				minWidth: isScreenMd ? 240 : '100%',
				width: isScreenMd ? 240 : '100%',
				marginBottom: isScreenMd ? 0 : 12,
				shadowColor: theme['shadow-color-100'],
			},
			totalValue: {
				marginLeft: isScreenMd ? 22 : 0,
			},
		}),
	};
}
